import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useCreditNote } from '../contexts/CreditNoteContext';
import { CreditNote } from '../../../types/accounting';
import CreateCreditNoteModal from './CreateCreditNoteModal';

interface CreditNotesModalProps {
  show: boolean;
  onHide: () => void;
}

export function CreditNotesModal({ show, onHide }: CreditNotesModalProps) {
  const { fetchCreditNotes, creditNotes, loading, deleteCreditNote } = useCreditNote();
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    if (show) {
      fetchCreditNotes();
    }
  }, [show, fetchCreditNotes]);

  const formatCurrency = (amount: number, currency = 'AUD') =>
    new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(amount);

  const formatDate = (d: string | undefined) => {
    if (!d) return '—';
    try { return new Date(d + 'T12:00:00Z').toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return d; }
  };

  const handleDelete = async (cn: CreditNote) => {
    if (!window.confirm(`Delete credit note ${cn.creditNoteNumber}?`)) return;
    try {
      await deleteCreditNote(cn.id);
    } catch (e) {
      console.error('Failed to delete credit note:', e);
    }
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" centered fullscreen="md-down">
        <Modal.Header closeButton>
          <Modal.Title>Credit notes</Modal.Title>
          <button className="btn btn-sm btn-success ms-2" onClick={() => setShowCreate(true)}>
            <i className="bi bi-plus-lg me-1" />
            New credit note
          </button>
        </Modal.Header>
        <Modal.Body>
          {loading ? (
            <div className="text-center py-5">
              <span className="spinner-border text-primary" />
              <p className="text-muted mt-2 mb-0">Loading credit notes...</p>
            </div>
          ) : creditNotes.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-receipt fs-1" />
              <p className="mt-3 mb-0">No credit notes found.</p>
              <button className="btn btn-sm btn-primary mt-3" onClick={() => setShowCreate(true)}>
                Create credit note
              </button>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-row-dashed align-middle">
                <thead>
                  <tr className="fw-bold text-muted">
                    <th>Number</th>
                    <th>Contact</th>
                    <th>Date</th>
                    <th>Total</th>
                    <th>Remaining</th>
                    <th>Status</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {creditNotes.map((cn: CreditNote) => (
                    <tr key={cn.id}>
                      <td className="fw-semibold">{cn.creditNoteNumber || cn.id.slice(0, 8)}</td>
                      <td>{cn.contactName || '-'}</td>
                      <td>{formatDate(cn.date)}</td>
                      <td>{formatCurrency(cn.total || 0)}</td>
                      <td>{formatCurrency(cn.remainingCredit ?? 0)}</td>
                      <td>
                        <span className={`badge ${cn.status === 'APPROVED' ? 'badge-light-primary' : cn.status === 'PAID' ? 'badge-light-success' : 'badge-light-secondary'}`}>
                          {cn.status}
                        </span>
                      </td>
                      <td className="text-end">
                        <button
                          className="btn btn-sm btn-icon btn-light-danger"
                          onClick={() => handleDelete(cn)}
                          title="Delete"
                        >
                          <i className="bi bi-trash" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <CreateCreditNoteModal
        show={showCreate}
        onHide={() => setShowCreate(false)}
        onSuccess={() => fetchCreditNotes()}
      />
    </>
  );
}

export default CreditNotesModal;
